import type {
  AdjustLimitParams,
  BatchToggleCustomerPermissionsParams,
  BatchToggleCustomerPermissionsResponse,
  BatchUpdateCustomerProfileParams,
  CustomerCompanyPermissions,
  CustomerDefaultPermissions,
  CustomerPermissionTreeNode,
  CustomerPermissionTreeResponse,
  SaveCustomerCompanyPermissionsParams,
  SaveCustomerDefaultPermissionsParams,
} from './type'

import type { PagingRequest } from '#/api/type'
import type {
  CustomerInfo,
  GetOperationUserListParams,
} from '#/types/key-features'

import { requestClient } from '#/api'
import { defineApi } from '#/api/define-api'

interface OperationUserListResponse {
  list?: OperationUserResponseItem[]
  total?: number
}

type OperationUserResponseItem = CustomerInfo & {
  creditAvailable?: number | string
  creditUsed?: number | string
  currBalance?: number | string
  preRechargeAvailable?: number | string
  preRechargeLimit?: number | string
}

function getOperationUserListParams(
  params: PagingRequest<GetOperationUserListParams>,
) {
  return {
    ...params.condition,
    userId: params.condition.userId,
    id: params.condition.id ? Number(params.condition.id) : undefined,
    email: params.condition.email,
    status: Number(params.condition.status),
    customerId: params.condition.customerId
      ? Number(params.condition.customerId)
      : undefined,
    customerIds:
      params.condition.customerIds && params.condition.customerIds.length > 0
        ? params.condition.customerIds.map(Number)
        : undefined,
    managerOpenId: params.condition.managerOpenId,
    operatorOpenId: params.condition.operatorOpenId,
    customerLevel: params.condition.customerLevel,
    companyStatus: Number(params.condition.companyStatus),
    identifier: params.condition.identifier,
  }
}

export const customerApi = defineApi('operation', ({ prefix }) => {
  function getOperationUserList(
    params: PagingRequest<GetOperationUserListParams>,
  ) {
    params.condition = getOperationUserListParams(params)
    return requestClient
      .post<OperationUserListResponse>(prefix('users/query'), {
        ...params,
      })
      .then((res) => {
        return {
          items:
            res?.list?.map((item) => ({
              ...item,
              currBalance: item.currBalance,
              creditUsed: item.creditUsed,
              creditAvailable: item.creditAvailable,
              preRechargeLimit: item.preRechargeLimit,
              preRechargeAvailable: item.preRechargeAvailable,
              companyStatus: String(item.companyStatus),
              status: String(item.status),
              customerLevel: item.customerLevel,
              operatorOpenId: item.operatorOpenId,
              managerOpenId: item.managerOpenId,
            })) || [],
          total: res?.total || 0,
        }
      })
  }
  // operation/users/create
  function createCustomer(params: CustomerInfo) {
    return requestClient.post(prefix('users/create'), {
      nickname: params.nickname,
      password: params.password,
      status: Number(params.status),
      communicationChannel: params.communicationChannel
        ? params.communicationChannel.map(Number)
        : [],
      communicationAccount: params.communicationAccount,
      mobile: params.mobile,
      source: Number(params.source),
      officialWeb: params.officialWeb,
      businessType: Number(params.businessType),
      cooperationStatus: Number(params.cooperationStatus),
      companyStatus: Number(params.companyStatus),
      customerLevel: params.customerLevel,
      email: params.email,
      currency: params.currency,
      operatorOpenId: params.operatorOpenId,
      managerOpenId: params.managerOpenId,
    })
  }

  function updateCustomer(params: CustomerInfo) {
    return requestClient.put(prefix('users/update'), {
      id: params.id,
      nickname: params.nickname,
      password: params.password,
      status: Number(params.status),
      communicationChannel: params.communicationChannel
        ? params.communicationChannel.map(Number)
        : [],
      communicationAccount: params.communicationAccount,
      mobile: params.mobile,
      source: Number(params.source),
      officialWeb: params.officialWeb,
      businessType: Number(params.businessType),
      cooperationStatus: Number(params.cooperationStatus),
      companyStatus: Number(params.companyStatus),
      customerLevel: params.customerLevel,
      currency: params.currency,
      operatorOpenId: params.operatorOpenId,
      managerOpenId: params.managerOpenId,
    })
  }

  function resetCustomerPassword(id: string) {
    return requestClient.put(prefix(`users/resetPassword/${id}`))
  }

  function updateCustomerEmail(params: { id: number; email: string }) {
    return requestClient.post(prefix('users/updateEmail'), params)
  }

  function adjustLimit(params: AdjustLimitParams) {
    return requestClient.post(prefix('transfer/adjustQuota'), params)
  }

  function batchUpdateProfile(params: BatchUpdateCustomerProfileParams) {
    return requestClient.post(prefix('users/batchUpdateProfile'), params)
  }

  function getCustomerDefaultPermissionTree() {
    return requestClient
      .get<CustomerPermissionTreeResponse>(prefix('custPerm/tree'))
      .then((data) =>
        data.menuTree.map(function mapNode(node): CustomerPermissionTreeNode {
          return {
            children: node.children?.map(mapNode),
            disabled: node.disabled,
            id: node.id,
            permissionFrontRoute: node.permissionFrontRoute,
            permissionMark: node.permissionMark,
            permissionName: node.permissionName,
            permissionParentId: node.permissionParentId,
            sort: node.sort,
            type: node.type,
          }
        }),
      )
  }

  function getCustomerDefaultPermissions() {
    return requestClient
      .get<CustomerDefaultPermissions>(prefix('custPerm/default'))
      .then((data) => ({ permissionIds: data.permissionIds }))
  }

  function saveCustomerDefaultPermissions(
    params: SaveCustomerDefaultPermissionsParams,
  ) {
    return requestClient.post(prefix('custPerm/default'), {
      permissionIds: params.permissionIds,
    })
  }

  function getCustomerCompanyPermissions(companyId: number) {
    return requestClient
      .get<CustomerCompanyPermissions>(prefix('custPerm/company'), {
        params: { companyId },
      })
      .then((data) => ({
        companyId: data.companyId,
        companyName: data.companyName,
        customerId: data.customerId,
        email: data.email,
        menuTree: data.menuTree.map(
          function mapNode(node): CustomerPermissionTreeNode {
            return {
              children: node.children?.map(mapNode),
              disabled: node.disabled,
              id: node.id,
              permissionFrontRoute: node.permissionFrontRoute,
              permissionMark: node.permissionMark,
              permissionName: node.permissionName,
              permissionParentId: node.permissionParentId,
              sort: node.sort,
              type: node.type,
            }
          },
        ),
        permissionIds: data.permissionIds,
      }))
  }

  function saveCustomerCompanyPermissions(
    params: SaveCustomerCompanyPermissionsParams,
  ) {
    return requestClient.post(prefix('custPerm/company'), {
      companyId: params.companyId,
      permissionIds: params.permissionIds,
    })
  }

  function batchToggleCustomerPermissions(
    params: BatchToggleCustomerPermissionsParams,
  ) {
    return requestClient
      .post<BatchToggleCustomerPermissionsResponse>(prefix('custPerm/batch'), {
        action: params.action,
        companyIds: params.companyIds,
        permissionIds: params.permissionIds,
      })
      .then((data) => ({
        failCount: data.failCount,
        successCount: data.successCount,
      }))
  }

  return {
    getOperationUserList,
    createCustomer,
    updateCustomer,
    resetCustomerPassword,
    updateCustomerEmail,
    adjustLimit,
    batchUpdateProfile,
    getCustomerDefaultPermissionTree,
    getCustomerDefaultPermissions,
    saveCustomerDefaultPermissions,
    getCustomerCompanyPermissions,
    saveCustomerCompanyPermissions,
    batchToggleCustomerPermissions,
    getOperationUserListParams,
  }
})
